import { useCallback, useMemo } from "react";

import { CTA_SLOT_LIMIT, FUND_STATUS } from "Giving/constants";

import { colorIsLight, ensureLeadingHex } from "Utilities/Colors/colorUtils";

import {
  useGetCampaigns,
  useGetCtas,
  useGetFundList,
  useGetMainSettings,
  useSetMediaSettings,
} from "./queries";

/**
 * Archived funds are hidden by default — they belong in totals/metadata but not
 * in the pickable/navigable fund lists most callers render. Callers that count
 * or check existence across all funds pass `includeArchived: true`.
 *
 * @return {FundList}
 */
const useFundList = ({ includeArchived = false } = {}) => {
  const { data: { funds = [] } = {} } = useGetFundList();

  return includeArchived
    ? funds
    : funds.filter((fund) => fund.status !== FUND_STATUS.ARCHIVED);
};

/** @return {{ campaigns: Campaign[], isLoading: boolean, error?: Error }} */
const useCampaigns = ({ enabled = true } = {}) => {
  const {
    data: { campaigns = [] } = {},
    isLoading,
    error,
  } = useGetCampaigns({ enabled });

  return { campaigns, isLoading, error };
};

const useBackgroundGradient = () => {
  const { data: { backgroundGradient = [] } = {} } = useGetMainSettings();

  return backgroundGradient;
};

const useBackgroundString = () => {
  const backgroundGradient = useBackgroundGradient();

  return useMemo(() => {
    const [one, two, three] = backgroundGradient.map((elem) =>
      ensureLeadingHex(elem)
    );
    return `linear-gradient(135deg, ${one}, ${two}, ${three})`;
  }, [backgroundGradient]);
};

const usePreviewColorVariant = () => {
  const [backgroundColor] = useBackgroundGradient();

  const previewColorVariant = useMemo(() => {
    const hexColor = ensureLeadingHex(backgroundColor);
    const isDark = !colorIsLight(hexColor);
    return isDark ? "dark" : "light";
  }, [backgroundColor]);

  return previewColorVariant;
};

const useCtaSlotsRemaining = () => {
  const { data: { ctas = [] } = {} } = useGetCtas();

  return useMemo(() => CTA_SLOT_LIMIT - ctas.length, [ctas]);
};

const useShowAddNewCta = () => {
  const ctaSlotsRemaining = useCtaSlotsRemaining();

  return useMemo(() => ctaSlotsRemaining > 0, [ctaSlotsRemaining]);
};

const useCtaOrder = () => {
  const { data: { ctas = [] } = {} } = useGetCtas();
  const ctaOrder = useMemo(() => ctas.map(({ ID }) => ID), [ctas]);

  return ctaOrder || [];
};

const useSetMediaType = () => {
  const { mutate: setMediaSettings } = useSetMediaSettings();
  return useCallback((type) => setMediaSettings({ type }), [setMediaSettings]);
};

export {
  useFundList,
  useCampaigns,
  useBackgroundGradient,
  useBackgroundString,
  usePreviewColorVariant,
  useCtaSlotsRemaining,
  useShowAddNewCta,
  useCtaOrder,
  useSetMediaType,
};
